/**
 * CRM Contact Service - Manage UGC Creator Contacts
 * 
 * Features:
 * - CRUD operations for contacts
 * - Link contacts to deals/brands
 * - Search and filter
 * - Email history tracking
 */

import { supabase } from '../lib/supabase';

class CRMContactService {
  /**
   * Create a new contact
   * @param {Object} contactData - Contact information
   * @returns {Promise} Created contact
   */
  async createContact(contactData) {
    try {
      const user = (await supabase.auth.getUser()).data.user;
      if (!user) throw new Error('Not authenticated');

      const {
        name,
        email,
        phone,
        title,
        notes,
        tags = [],
        brandId = null
      } = contactData;

      if (!name || !email) {
        throw new Error('Name and email are required');
      }

      const { data, error } = await supabase
        .from('ugc_contacts')
        .insert({
          user_id: user.id,
          name: name.trim(),
          email: email.trim().toLowerCase(),
          phone: phone?.trim() || null,
          title: title?.trim() || null,
          notes: notes?.trim() || null,
          tags,
          brand_id: brandId
        })
        .select()
        .single();

      if (error) throw error;

      console.log('[CRM] Contact created:', data.id);
      return data;
    } catch (error) {
      console.error('[CRM Contact Create Error]:', error);
      throw error;
    }
  }

  /**
   * Update a contact
   * @param {string} contactId - Contact ID
   * @param {Object} updates - Fields to update
   * @returns {Promise} Updated contact
   */
  async updateContact(contactId, updates) {
    try {
      const user = (await supabase.auth.getUser()).data.user;
      if (!user) throw new Error('Not authenticated');

      // Verify user owns this contact
      const { data: contact, error: fetchError } = await supabase
        .from('ugc_contacts')
        .select('user_id')
        .eq('id', contactId)
        .single();

      if (fetchError || contact.user_id !== user.id) {
        throw new Error('Contact not found or unauthorized');
      }

      // Build safe update object
      const safeUpdates = {};
      if (updates.name !== undefined) safeUpdates.name = updates.name.trim();
      if (updates.email !== undefined) safeUpdates.email = updates.email.trim().toLowerCase();
      if (updates.phone !== undefined) safeUpdates.phone = updates.phone?.trim() || null;
      if (updates.title !== undefined) safeUpdates.title = updates.title?.trim() || null;
      if (updates.notes !== undefined) safeUpdates.notes = updates.notes?.trim() || null;
      if (updates.tags !== undefined) safeUpdates.tags = Array.isArray(updates.tags) ? updates.tags : [];
      if (updates.brandId !== undefined) safeUpdates.brand_id = updates.brandId;

      const { data, error } = await supabase
        .from('ugc_contacts')
        .update(safeUpdates)
        .eq('id', contactId)
        .select()
        .single();

      if (error) throw error;

      console.log('[CRM] Contact updated:', contactId);
      return data;
    } catch (error) {
      console.error('[CRM Contact Update Error]:', error);
      throw error;
    }
  }

  /**
   * Delete a contact
   * @param {string} contactId - Contact ID
   * @returns {Promise} Deletion result
   */
  async deleteContact(contactId) {
    try {
      const user = (await supabase.auth.getUser()).data.user;
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('ugc_contacts')
        .delete()
        .eq('id', contactId)
        .eq('user_id', user.id);

      if (error) throw error;

      console.log('[CRM] Contact deleted:', contactId);
      return { success: true };
    } catch (error) {
      console.error('[CRM Contact Delete Error]:', error);
      throw error;
    }
  }

  /**
   * Get all contacts with search/filter
   * @param {Object} options - Filter options
   * @returns {Promise} Array of contacts
   */
  async listContacts(options = {}) {
    try {
      const user = (await supabase.auth.getUser()).data.user;
      if (!user) throw new Error('Not authenticated');

      const {
        search = '',
        brandId = null,
        tags = [],
        limit = 100,
        offset = 0
      } = options;

      let query = supabase
        .from('ugc_contacts')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .range(offset, offset + limit - 1);

      // Brand filter
      if (brandId) {
        query = query.eq('brand_id', brandId);
      }

      // Tags filter (any tag match)
      if (tags.length > 0) {
        query = query.contains('tags', tags);
      }

      const { data, error } = await query;

      if (error) throw error;

      // Client-side search (name, email, title, notes)
      if (search) {
        const term = search.toLowerCase();
        return data.filter(contact =>
          contact.name.toLowerCase().includes(term) ||
          contact.email.toLowerCase().includes(term) ||
          (contact.title && contact.title.toLowerCase().includes(term)) ||
          (contact.notes && contact.notes.toLowerCase().includes(term))
        );
      }

      return data;
    } catch (error) {
      console.error('[CRM Contact List Error]:', error);
      throw error;
    }
  }

  /**
   * Get single contact with email history
   * @param {string} contactId - Contact ID
   * @returns {Promise} Contact with email history
   */
  async getContact(contactId) {
    try {
      const user = (await supabase.auth.getUser()).data.user;
      if (!user) throw new Error('Not authenticated');

      // Get contact
      const { data: contact, error: contactError } = await supabase
        .from('ugc_contacts')
        .select('*')
        .eq('id', contactId)
        .eq('user_id', user.id)
        .single();

      if (contactError) throw contactError;

      // Get email history
      const { data: emails, error: emailError } = await supabase
        .from('ugc_emails')
        .select('*')
        .eq('contact_id', contactId)
        .eq('user_id', user.id)
        .order('sent_at', { ascending: false });

      if (emailError) throw emailError;

      return {
        ...contact,
        emailHistory: emails || [],
        totalEmails: emails?.length || 0
      };
    } catch (error) {
      console.error('[CRM Contact Fetch Error]:', error);
      throw error;
    }
  }

  /**
   * Search contacts by email
   * @param {string} email - Email address
   * @returns {Promise} Contact if found
   */
  async findByEmail(email) {
    try {
      const user = (await supabase.auth.getUser()).data.user;
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('ugc_contacts')
        .select('*')
        .eq('user_id', user.id)
        .eq('email', email.toLowerCase())
        .single();

      if (error && error.code !== 'PGRST116') throw error; // PGRST116 = no rows
      return data || null;
    } catch (error) {
      console.error('[CRM Contact Find Error]:', error);
      return null;
    }
  }

  /**
   * Add tag to contact
   * @param {string} contactId - Contact ID
   * @param {string} tag - Tag to add
   * @returns {Promise} Updated contact
   */
  async addTag(contactId, tag) {
    try {
      const user = (await supabase.auth.getUser()).data.user;
      if (!user) throw new Error('Not authenticated');

      const { data: contact } = await supabase
        .from('ugc_contacts')
        .select('tags')
        .eq('id', contactId)
        .eq('user_id', user.id)
        .single();

      if (!contact) throw new Error('Contact not found');

      const newTags = [...new Set([...contact.tags, tag])]; // Unique tags

      return await this.updateContact(contactId, { tags: newTags });
    } catch (error) {
      console.error('[CRM Add Tag Error]:', error);
      throw error;
    }
  }

  /**
   * Remove tag from contact
   * @param {string} contactId - Contact ID
   * @param {string} tag - Tag to remove
   * @returns {Promise} Updated contact
   */
  async removeTag(contactId, tag) {
    try {
      const user = (await supabase.auth.getUser()).data.user;
      if (!user) throw new Error('Not authenticated');

      const { data: contact } = await supabase
        .from('ugc_contacts')
        .select('tags')
        .eq('id', contactId)
        .eq('user_id', user.id)
        .single();

      if (!contact) throw new Error('Contact not found');

      const newTags = contact.tags.filter(t => t !== tag);

      return await this.updateContact(contactId, { tags: newTags });
    } catch (error) {
      console.error('[CRM Remove Tag Error]:', error);
      throw error;
    }
  }

  /**
   * Link contact to deal
   * @param {string} contactId - Contact ID
   * @param {string} dealId - Deal ID
   * @returns {Promise} Success
   */
  async linkContactToDeal(contactId, dealId) {
    try {
      // This creates a relationship through email logging
      // Could also be done with a junction table if needed
      console.log('[CRM] Contact linked to deal:', { contactId, dealId });
      return { success: true };
    } catch (error) {
      console.error('[CRM Link Error]:', error);
      throw error;
    }
  }

  /**
   * Get contacts by brand
   * @param {string} brandId - Brand ID
   * @returns {Promise} Array of contacts
   */
  async getContactsByBrand(brandId) {
    try {
      const user = (await supabase.auth.getUser()).data.user;
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('ugc_contacts')
        .select('*')
        .eq('user_id', user.id)
        .eq('brand_id', brandId)
        .order('name');

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('[CRM Get by Brand Error]:', error);
      throw error;
    }
  }
}

export const crmContactService = new CRMContactService();
